import React, { forwardRef, useEffect } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableHead, 
  TableRow, Paper, Grid, Divider, Chip
} from '@mui/material';

const AdminFinancialReport = forwardRef(({ 
  tenant, 
  members, 
  memberDebts,
  memberTransactions,
  financialSummary,
  expenses,
  stats,
  reportType = 'all' 
}, ref) => {
  // Add logging to debug tenant name issue
  useEffect(() => {
    console.log('Tenant data received in report:', tenant);
  }, [tenant]);

  // Generate formatted report date
  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate total expenses
  const totalExpenses = expenses ? expenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0) : 0;

  // Calculate net financial position (revenue minus expenses)
  const netRevenue = (financialSummary.totalPaymentsReceived || stats?.totalPayments || 0) - totalExpenses;

  // Enhanced helper function to safely extract tenant name
  const getTenantName = () => {
    // Log the tenant object for debugging
    console.log('Trying to extract name from tenant:', tenant);
    
    // Check for null or undefined
    if (!tenant) return 'Organization';
    
    // Handle case where tenant is a string
    if (typeof tenant === 'string') return tenant;
    
    // Handle tenant object depending on its structure
    if (typeof tenant === 'object') {
      // Direct name property
      if (tenant.name) {
        console.log('Found name directly:', tenant.name);
        return tenant.name;
      }
      
      // Inside data property
      if (tenant.data && tenant.data.name) {
        console.log('Found name in data:', tenant.data.name);
        return tenant.data.name;
      }
      
      // Inside settings property
      if (tenant.settings && tenant.settings.name) {
        console.log('Found name in settings:', tenant.settings.name);
        return tenant.settings.name;
      }
      
      // Try accessing tenant.tenant.name (nested structure)
      if (tenant.tenant && tenant.tenant.name) {
        console.log('Found name in tenant.tenant:', tenant.tenant.name);
        return tenant.tenant.name;
      }
      
      // Try accessing through any property that might contain the name
      for (const key in tenant) {
        if (typeof tenant[key] === 'object' && tenant[key] && tenant[key].name) {
          console.log(`Found name in tenant.${key}:`, tenant[key].name);
          return tenant[key].name;
        }
      }
      
      // Last resort: try to find any property that contains the word "name"
      for (const key in tenant) {
        if (typeof tenant[key] === 'string' && key.toLowerCase().includes('name')) {
          console.log(`Found potential name in property ${key}:`, tenant[key]);
          return tenant[key];
        }
      }
    }
    
    console.log('Could not find tenant name, defaulting to Organization');
    return 'Organization';
  };

  // Get the tenant name once for consistent use throughout the report
  const tenantName = getTenantName();

  // Group expenses by category
  const expensesByCategory = {};
  if (expenses && expenses.length > 0) {
    expenses.forEach(expense => {
      const category = expense.category || 'Other';
      if (!expensesByCategory[category]) {
        expensesByCategory[category] = {
          count: 0,
          total: 0
        };
      }
      expensesByCategory[category].count += 1;
      expensesByCategory[category].total += parseFloat(expense.amount) || 0;
    });
  }

  // Get category label from category value
  const getCategoryLabel = (categoryValue) => {
    const categories = {
      utilities: 'Utilities',
      rent: 'Rent/Lease',
      salaries: 'Salaries/Wages',
      supplies: 'Office Supplies',
      equipment: 'Equipment',
      marketing: 'Marketing/Advertising',
      maintenance: 'Maintenance',
      events: 'Events',
      transportation: 'Transportation',
      insurance: 'Insurance',
      professional_fees: 'Professional Fees',
      taxes: 'Taxes',
      meals: 'Meals/Entertainment',
      other: 'Other'
    };

    return categories[categoryValue] || categoryValue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box ref={ref} sx={{ 
      p: 4, 
      width: '210mm',
      minHeight: '297mm',
      backgroundColor: 'white',
      color: 'black',
      fontFamily: 'Arial, sans-serif',
      fontSize: '10pt'
    }}>
      {/* Report Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#3f51b5' }}>
          Financial Transaction Report
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          {tenantName} Management System
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Generated on {reportDate}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Organization Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Organization Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Name:</strong> {tenantName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Total Members:</strong> {stats?.totalMembers || members?.length || 0}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Active Members:</strong> {stats?.activeMembers || members?.filter(m => m.status === 'active')?.length || 0}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Report Type:</strong> {
                reportType === 'all' ? 'Complete Financial Report' :
                reportType === 'payments' ? 'Payment Summary' :
                reportType === 'debts' ? 'Outstanding Debts' : 
                reportType === 'expenses' ? 'Expenses Summary' : 'Member Breakdown'
              }
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Date Range:</strong> All Time
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Report ID:</strong> FIN-{Math.random().toString(36).substring(2, 10).toUpperCase()}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Financial Summary */}
      {(reportType === 'all' || reportType === 'payments') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Financial Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Total Revenue
                </Typography>
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  {formatCurrency(financialSummary.totalPaymentsReceived || stats?.totalPayments)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Total Expenses
                </Typography>
                <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                  {formatCurrency(totalExpenses)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Net Revenue
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: netRevenue >= 0 ? '#1565c0' : '#d32f2f', 
                  fontWeight: 'bold' 
                }}>
                  {formatCurrency(netRevenue)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Net Financial Position
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Revenue - Expenses:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ 
                    color: netRevenue >= 0 ? '#2e7d32' : '#d32f2f'
                  }}>
                    {formatCurrency(netRevenue)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding Debt Ratio:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(financialSummary.totalDebts)} / {formatCurrency(financialSummary.totalPaymentsReceived)}
                    {' '}
                    ({financialSummary.totalPaymentsReceived ? 
                      ((financialSummary.totalDebts / financialSummary.totalPaymentsReceived) * 100).toFixed(1) : 0}%)
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Box>
      )}

      {/* Expenses Summary */}
      {(reportType === 'all' || reportType === 'expenses') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f44336', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Expenses Summary
          </Typography>
          
          {expenses && expenses.length > 0 ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Expenses
                    </Typography>
                    <Typography variant="h5" color="error.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {formatCurrency(totalExpenses)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Number of expense records: {expenses.length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Expenses by Category
                    </Typography>
                    <Box sx={{ maxHeight: '150px', overflowY: 'auto', pr: 1 }}>
                      {Object.entries(expensesByCategory).map(([category, data], index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">
                            {getCategoryLabel(category)}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(data.total)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Recent Expenses
              </Typography>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#ffebee' }}>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Payment Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.slice(0, 10).map((expense, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                      <TableCell>{formatDate(expense.date)}</TableCell>
                      <TableCell>{getCategoryLabel(expense.category)}</TableCell>
                      <TableCell>{expense.description || 'N/A'}</TableCell>
                      <TableCell sx={{ color: '#d32f2f', fontWeight: 'medium' }}>
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell>
                        {expense.paymentMethod?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {expenses.length > 10 && (
                <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                  Showing 10 of {expenses.length} expenses.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
              No expense records found.
            </Typography>
          )}
        </Box>
      )}

      {/* Outstanding Debts */}
      {(reportType === 'all' || reportType === 'debts') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f44336', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Outstanding Debts
          </Typography>
          
          {Object.keys(memberDebts).length > 0 ? (
            <Table sx={{ mb: 2 }}>
              <TableHead sx={{ bgcolor: '#ffebee' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Number of Debts</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Due Dates</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(memberDebts)
                  .filter(([_, debts]) => debts.filter(d => d.status === 'active').length > 0)
                  .map(([memberId, debts]) => {
                    const member = members.find(m => m._id === memberId);
                    const activeDebts = debts.filter(d => d.status === 'active');
                    const totalDebt = activeDebts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
                    
                    if (!member || activeDebts.length === 0) return null;
                    
                    return (
                      <TableRow key={memberId} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                        <TableCell>
                          {member.userId?.firstName} {member.userId?.lastName}
                        </TableCell>
                        <TableCell>
                          <Box 
                            sx={{ 
                              display: 'inline-block', 
                              px: 1, 
                              py: 0.5, 
                              borderRadius: '4px', 
                              fontSize: '0.8rem',
                              fontWeight: 'bold',
                              bgcolor: member.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 
                                      member.status === 'pending' ? 'rgba(255, 152, 0, 0.1)' : 
                                      'rgba(244, 67, 54, 0.1)',
                              color: member.status === 'active' ? '#4caf50' : 
                                     member.status === 'pending' ? '#ff9800' : 
                                     '#f44336',
                              textTransform: 'uppercase'
                            }}
                          >
                            {member.status || 'UNKNOWN'}
                          </Box>
                        </TableCell>
                        <TableCell>{activeDebts.length}</TableCell>
                        <TableCell sx={{ color: '#d32f2f', fontWeight: 'medium' }}>
                          {formatCurrency(totalDebt)}
                        </TableCell>
                        <TableCell>
                          {activeDebts.slice(0, 2).map((debt, idx) => (
                            <Box key={idx} sx={{ mb: idx < activeDebts.length - 1 ? 0.5 : 0 }}>
                              {formatDate(debt.dueDate)}
                            </Box>
                          ))}
                          {activeDebts.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{activeDebts.length - 2} more
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
              No outstanding debts found.
            </Typography>
          )}
        </Box>
      )}

      {/* Member Financial Breakdown */}
      {(reportType === 'all' || reportType === 'members') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Member Financial Breakdown
          </Typography>
          
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#e8eaf6' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Payments Received</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Outstanding Debts</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Money Sent</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Net Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => {
                // Calculate payments received
                const payments = member.payments || [];
                const paymentsTotal = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
                
                // Calculate active debts
                const debts = memberDebts[member._id] || [];
                const activeDebts = debts.filter(d => d.status === 'active');
                const debtsTotal = activeDebts.reduce((sum, d) => sum + (d.amount || 0), 0);
                
                // Calculate total sent to this member
                const transactions = memberTransactions[member._id] || [];
                const sentTotal = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
                
                // Calculate net balance
                const netBalance = paymentsTotal - sentTotal;
                
                return (
                  <TableRow key={member._id} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                    <TableCell>
                      {member.userId?.firstName} {member.userId?.lastName}
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'inline-block', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: '4px', 
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          bgcolor: member.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 
                                  member.status === 'pending' ? 'rgba(255, 152, 0, 0.1)' : 
                                  'rgba(244, 67, 54, 0.1)',
                          color: member.status === 'active' ? '#4caf50' : 
                                 member.status === 'pending' ? '#ff9800' : 
                                 '#f44336',
                          textTransform: 'uppercase'
                        }}
                      >
                        {member.status || 'UNKNOWN'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#2e7d32' }}>
                      {formatCurrency(paymentsTotal)}
                    </TableCell>
                    <TableCell sx={{ color: debtsTotal > 0 ? '#d32f2f' : 'text.secondary' }}>
                      {formatCurrency(debtsTotal)}
                    </TableCell>
                    <TableCell sx={{ color: sentTotal > 0 ? '#0277bd' : 'text.secondary' }}>
                      {formatCurrency(sentTotal)}
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 'medium',
                      color: netBalance > 0 ? '#2e7d32' : 
                              netBalance < 0 ? '#d32f2f' : 'text.secondary'
                    }}>
                      {formatCurrency(netBalance)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Recent Payment Activity */}
      {(reportType === 'all' || reportType === 'payments') && stats?.recentTransactions?.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#009688', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Recent Payment Activity
          </Typography>
          
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#e0f2f1' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.recentTransactions.slice(0, 10).map((transaction, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>{formatDate(transaction.date || transaction.transactionDate || transaction.createdAt)}</TableCell>
                  <TableCell>{transaction.memberName || (transaction.member && `${transaction.member.firstName} ${transaction.member.lastName}`) || 'N/A'}</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'medium', 
                    color: transaction.type === 'refund' ? '#d32f2f' : '#2e7d32' 
                  }}>
                    {formatCurrency(transaction.amount || 0)}
                  </TableCell>
                  <TableCell>
                    {transaction.type || 'payment'}
                  </TableCell>
                  <TableCell>
                    {(transaction.method || transaction.paymentMethod || 'N/A').replace('_', ' ')}
                  </TableCell>
                  <TableCell>{transaction.description || transaction.notes || 'No description'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {stats.recentTransactions.length > 10 && (
            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
              Showing 10 of {stats.recentTransactions.length} recent transactions
            </Typography>
          )}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This is an official financial report generated from the {tenantName} management system.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
          Report ID: {Math.random().toString(36).substr(2, 9).toUpperCase()} | Page 1
        </Typography>
      </Box>
    </Box>
  );
});

AdminFinancialReport.displayName = 'AdminFinancialReport';

export default AdminFinancialReport;
